import React, {
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { Alert, Spin, Card, Typography, Select } from "antd";
import { TagOutlined } from "@ant-design/icons";
import {
  searchForGroup,
  searchForGroupExternal,
  getIdentityById,
} from "../../actions/api";
import { clearSearch } from "../../actions";
import { types, errorAction } from "@authzsvc/api-lib";
import { isApiCallLoading, userTokenHasRole } from "../../selectors";
import { ContentTable, SearchBar } from "@authzsvc/common-react-components";
import {
  defaultGroupTableColumns,
  externalGroupTableColumns,
  DEFAULT_GROUP_TABLE_QUERY_FIELDS,
  searchMaxLimit,
} from "../../constants";
import SubscriptionButton from "../group/SubscriptionButton";
import IdentitySearchSelect from "../common/IdentitySearchSelect";
import styles from "./Dashboard.module.css";
import { showErrorNotification } from "../../util";

const Dashboard = () => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  const searchCriteriaOptions = [
    { key: "groupIdentifier", label: "group identifier" },
    { key: "alias", label: "mailing list alias" },
  ];

  // Initialize state from URL parameters or defaults
  const [searchValue, setSearchValue] = useState(
    searchParams.get("search") || ""
  );
  const [currentPage, setCurrentPage] = useState(() =>
    parseInt(sessionStorage.getItem("dashboard_currentPage") || "1", 10)
  );
  const isFirstFetch = useRef(true);
  const [currentFilter, setCurrentFilter] = useState(
    searchParams.get("filter") || "Contains"
  );
  const [selectedSearchCriteria, setSelectedSearchCriteria] = useState(
    searchParams.get("criteria") || "groupIdentifier"
  );

  const [selectedIdentity, setSelectedIdentity] = useState(null);
  const [isInitializedFromUrl, setIsInitializedFromUrl] = useState(false);
  const lastSearchRef = React.useRef(null);

  // Load identity from URL if present and trigger search
  useEffect(() => {
    const identityId = searchParams.get("identityId");
    if (identityId && !isInitializedFromUrl) {
      // Fetch full identity details from API
      const fetchIdentity = async () => {
        try {
          const result = await dispatch(getIdentityById(identityId, {}));
          if (result && result.payload && result.payload.data) {
            setSelectedIdentity(result.payload.data);
          }
        } catch (error) {
          console.error("Failed to load identity:", error);
        }
        setIsInitializedFromUrl(true);
      };
      fetchIdentity();
    } else if (!identityId && !isInitializedFromUrl) {
      // Mark as initialized even if no identity ID
      setIsInitializedFromUrl(true);
    }
  }, [searchParams, isInitializedFromUrl, dispatch]);

  const groupSearch = useSelector((state) => state.groups.groupSearch);
  const loadingSearch = useSelector((state) =>
    isApiCallLoading(state, types.SEARCH_GROUP)
  );
  const groupMemberships = useSelector((state) => state.groupMembership.direct);
  const identity = useSelector((state) => state.identity);
  const isExternal = useSelector(
    (state) => !userTokenHasRole(state, "group_reader")
  );

  const handleInputChange = (value) => {
    setSearchValue(value);
    if ((!value || value.trim().length === 0) && selectedIdentity?.id) {
      handleSearch("", currentFilter, undefined, selectedSearchCriteria);
    }
  };

  const handleSearchCriteriaChange = (value) => {
    setSelectedSearchCriteria(value);
    if (searchValue?.length >= 2) {
      handleSearch(searchValue, currentFilter, undefined, value);
    }
  };

  const handleIdentitySelect = async (identity) => {
    setSelectedIdentity(identity);
    await handleSearch(
      searchValue,
      currentFilter,
      undefined,
      selectedSearchCriteria,
      identity.id
    );
  };

  const clearIdentityFilter = () => {
    setSelectedIdentity(null);
    // Only trigger a search if there's a non-empty main search value
    if (searchValue && searchValue.trim().length > 0) {
      handleSearch(
        searchValue,
        currentFilter,
        undefined,
        selectedSearchCriteria,
        null
      );
    }
  };

  const searchCriteriaSelector = (
    <Select
      value={selectedSearchCriteria}
      onChange={handleSearchCriteriaChange}
      options={searchCriteriaOptions.map((opt) => ({
        value: opt.key,
        label: opt.label,
      }))}
      style={{
        borderRight: "1px solid #d9d9d9",
        marginRight: 0,
      }}
      popupMatchSelectWidth={false}
      suffixIcon={<TagOutlined />}
    />
  );

  const disableSubscription = useCallback(
    (group) => {
      let disabledSubscription = {
        disabled: false,
        message: "Modify group subscription",
      };

      if (group.selfSubscriptionType === "Closed") {
        disabledSubscription.disabled = true;
        disabledSubscription.message =
          "This group does not allow self subscription.";
      } else if (group.syncType !== "NoSync" && group.syncType !== "Master") {
        disabledSubscription.disabled = true;
        disabledSubscription.message =
          "This group is managed via E-groups, please visit e-groups.cern.ch to subscribe";
      } else if (
        group.owner &&
        group.owner.id === identity &&
        disabledSubscription.disabled
      ) {
        disabledSubscription.disabled = false;
        disabledSubscription.message = "Modify group subscription";
      } else if (
        groupMemberships &&
        groupMemberships.find((g) => g.id === group.id) &&
        disabledSubscription.disabled
      ) {
        disabledSubscription.disabled = false;
        disabledSubscription.message = "Modify group subscription";
      }

      return disabledSubscription;
    },
    [identity, groupMemberships]
  );

  const groupColumns = useMemo(
    () => [
      ...(isExternal ? externalGroupTableColumns : defaultGroupTableColumns),
      {
        title: "Action",
        render: (_, group) => {
          const disabled = disableSubscription(group);
          if (group.dynamic) return;
          return (
            <SubscriptionButton
              group={group}
              disabled={disabled.disabled}
              tooltipMessage={disabled.message}
            />
          );
        },
      },
    ],
    [disableSubscription, isExternal]
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleSearch = useCallback(
    async (
      text,
      filter,
      limit,
      searchCriteria = selectedSearchCriteria,
      ownerIdArg = undefined
    ) => {
      // Capture the current filter value from SearchBar
      setCurrentFilter(filter);

      const groupTableQueryFields = [
        ...DEFAULT_GROUP_TABLE_QUERY_FIELDS,
        "selfSubscriptionType",
        "approvalRequired",
        "administratorsId",
      ];

      const effectiveOwnerId =
        ownerIdArg !== undefined ? ownerIdArg : selectedIdentity?.id;
      const filters = [];
      if (effectiveOwnerId) filters.push(`ownerId:Eq:${effectiveOwnerId}`);
      if (text && text.trim().length > 0)
        filters.push(`${searchCriteria}:${filter}:${text.trim()}`);

      const searchParams = {
        sort: "groupIdentifier",
        field: groupTableQueryFields,
      };

      // Deduplicate identical consecutive searches
      const searchKey = JSON.stringify({
        text: text?.trim() || "",
        filter: filter || null,
        owner: effectiveOwnerId || null,
        criteria: searchCriteria,
        limit: limit != null ? limit : null,
      });
      if (lastSearchRef.current === searchKey) return;
      lastSearchRef.current = searchKey;

      if (isFirstFetch.current) {
        isFirstFetch.current = false;
      } else {
        setCurrentPage(1);
      }

      if (filters.length > 0) {
        searchParams.filter = filters.length === 1 ? filters[0] : filters;
      }

      if (limit != null) {
        searchParams.limit = limit;
      }

      const response = dispatch(
        isExternal
          ? searchForGroupExternal(searchParams)
          : searchForGroup(searchParams)
      );

      if (response.type === errorAction(types.SEARCH_GROUP)) {
        showErrorNotification(
          response.payload.title,
          response.payload.data.message
        );
      }
    },
    [dispatch, selectedSearchCriteria, isExternal, selectedIdentity?.id]
  );

  // Trigger search when URL parameters are loaded
  useEffect(() => {
    if (isInitializedFromUrl && searchValue) {
      const identityId = searchParams.get("identityId");
      handleSearch(
        searchValue,
        currentFilter,
        undefined,
        selectedSearchCriteria,
        identityId || undefined
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInitializedFromUrl]);

  // Update URL when search parameters change (debounced)
  useEffect(() => {
    // Skip updating URL during initial load from URL
    if (!isInitializedFromUrl) return;

    const timer = setTimeout(() => {
      const newParams = new URLSearchParams();
      if (searchValue) newParams.set("search", searchValue);
      // Always include filter and criteria
      newParams.set("filter", currentFilter);
      newParams.set("criteria", selectedSearchCriteria);
      // Use the ID from selectedIdentity (works for both minimal and full objects)
      if (selectedIdentity?.id)
        newParams.set("identityId", selectedIdentity.id);

      // Only update if parameters actually changed
      const currentParamsString = searchParams.toString();
      const newParamsString = newParams.toString();
      if (currentParamsString !== newParamsString) {
        setSearchParams(newParams, { replace: true });
      }
    }, 500); // Debounce for 500ms

    return () => clearTimeout(timer);
  }, [
    searchValue,
    currentFilter,
    selectedSearchCriteria,
    selectedIdentity,
    searchParams,
    setSearchParams,
    isInitializedFromUrl,
  ]);

  useEffect(() => {
    sessionStorage.setItem("dashboard_currentPage", currentPage);
  }, [currentPage]);

  const groupSearchMapped = groupSearch.map((gr) => ({ ...gr, key: gr.id }));

  useEffect(() => {
    dispatch(clearSearch());
    return () => {
      dispatch(clearSearch());
    };
  }, [dispatch]);

  return (
    <Card
      title={
        <Typography.Title level={2} style={{ textAlign: "center", margin: 0 }}>
          Group Management System
        </Typography.Title>
      }
    >
      <Spin spinning={loadingSearch}>
        <div
          style={{ marginBottom: 24 }}
          aria-label="Search groups"
          role="search"
        >
          <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
            <div style={{ flex: 1 }}>
              <SearchBar
                className={styles.searchBar}
                onSearch={handleSearch}
                searchValue={searchValue}
                onSearchValueChange={handleInputChange}
                filter={true}
                filterPlacement="before"
                searchOnClear={false}
                allowClear={true}
                addonBefore={searchCriteriaSelector}
                autoFocus={true}
              />
            </div>
            <div style={{ minWidth: 240, flex: "0 0 240px" }}>
              <IdentitySearchSelect
                placeholder="Search by owner"
                onSelect={handleIdentitySelect}
                onClear={clearIdentityFilter}
                value={selectedIdentity}
                style={{ width: "100%" }}
                searchFilter="source:cern"
              />
            </div>
          </div>
        </div>
        {groupSearchMapped.length >= searchMaxLimit && (
          <Alert
            message="The search returned the maximum number of results. There might be more results that are not displayed. Please refine your search criteria."
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}
        <ContentTable
          pagination={{
            current: currentPage,
            onChange: (page) => setCurrentPage(page),
            showSizeChanger: true,
            hideOnSinglePage: true,
            pageSizeOptions: ["50", "100", "500"],
            defaultPageSize: "50",
          }}
          columns={groupColumns}
          dataSource={groupSearchMapped.length > 0 ? groupSearchMapped : null}
        />
      </Spin>
    </Card>
  );
};

export default Dashboard;
